import ListoryStore from '@/store';
import { Poi, PoiState, PoiJournalEntry } from '@/models/poi';
import ProgressCalculator from '@/services/progress-calculator';
import Vue from 'vue';

export default class PoiJournalService {
    private store: ListoryStore;

    constructor(store: ListoryStore) {
        this.store = store;
    }

    public async setPoiReadyToUnlock(poi: Poi, position?: Position) {
        if (!this.isInStore(poi.id)) return;
        if (poi.state !== PoiState.LOCKED) {
            console.error('POI is not locked: ', poi);
            return;
        }

        const entry = this.createJournalEntry(poi, PoiState.READY_TO_UNLOCK, position);
        this.store.poiJournal.push(entry);

        Vue.set(poi, 'state', PoiState.READY_TO_UNLOCK);
        await this.store.saveState();
        this.store.$emit('poiReadyToUnlock', poi);
    }

    public async setPoiUnlocked(poi: Poi, position?: Position) {
        if (!this.isInStore(poi.id)) return;
        if (poi.state !== PoiState.READY_TO_UNLOCK) {
            console.error('POI is not ready to unlock: ', poi);
            return;
        }

        // Calculate badges before unlock
        const progress = new ProgressCalculator(this.store);
        progress.updateBadges();

        const entry = this.createJournalEntry(poi, PoiState.UNLOCKED, position);
        this.store.poiJournal.push(entry);

        Vue.set(poi, 'state', PoiState.UNLOCKED);
        await this.store.saveState();
        this.store.$emit('poiUnlocked', poi);

        // Emit event if a new badge was unlocked
        const unlockedBadges = progress.updateBadges();
        for (const badge of unlockedBadges) {
            this.store.$emit('badgeAchieved', badge);
        }
    }

    public restorePoiStates() {
        this.resetPoiStates();

        for (const journalEntry of this.store.poiJournal) {
            const poi = this.store.getPoi(journalEntry.poi_id);
            if (poi === undefined) continue;

            if (poi.state !== journalEntry.old_state) {
                console.error('INCONSISTENT JOURNAL ENTRY: ', journalEntry);
            }

            poi.state = journalEntry.new_state;
        }
    }

    private isInStore(id: number) {
        const poi = this.store.getPoi(id);
        if (poi === undefined) {
            console.error('POI not managed by this store!');
        }
        return poi !== undefined;
    }

    private resetPoiStates() {
        for (const poi of this.store.pois) {
            poi.state = PoiState.LOCKED;
            poi.distance = 0;
        }
    }

    public createJournalEntry(poi: Poi, state: PoiState, position?: Position): PoiJournalEntry {
        const change = {
            poi_id: poi.id,
            old_state: poi.state,
            new_state: state,
            time: new Date(),
            position: []
        };

        if (position && position.coords) {
            change.position = [position.coords.longitude, position.coords.latitude];
        }

        return change;
    }
}
